import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddDeudaInput,
  ClientCacheRow,
  ClientSnapshot,
  ClientStatus,
  DiasInput,
  PagarInput,
  RevertirInput,
} from '@/shared/types';
import { api } from '@/lib/api';

export interface ClientsQuery {
  status?: ClientStatus;
  server_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

interface ClientsListResp {
  rows: ClientCacheRow[];
  page: number;
  page_size: number;
  total: number;
}

function qs(o: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function useClientsList(query: ClientsQuery) {
  return useQuery({
    queryKey: ['clients', query],
    queryFn: () => api<ClientsListResp>(`/clients${qs(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useClientSnapshot(serverId: string | null, dbName: string | null) {
  return useQuery({
    queryKey: ['client-snapshot', serverId, dbName],
    queryFn: () => api<ClientSnapshot>(`/clients/${serverId}/${dbName}/snapshot`),
    enabled: Boolean(serverId && dbName),
    staleTime: 0,
  });
}

interface MutationCtx {
  serverId: string;
  dbName: string;
}

/**
 * After any mutation we need to refresh THREE caches:
 *  - `client-snapshot` → proxies fresh to the upstream PHP, so the inline
 *    accordion (alargar_dias, deudas, estados) updates immediately.
 *  - `clients` → reads from clients_cache in Postgres. That cache only updates
 *    AFTER the queue's sync-client job finishes (1-3s). So we invalidate now
 *    AND again ~3s later, otherwise the collapsed row shows stale data.
 *  - `dashboard` → KPIs include this row.
 */
function refreshAfterMutation(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['client-snapshot'] });
  qc.invalidateQueries({ queryKey: ['clients'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
  // Re-invalidate after the worker is likely done so the cache row catches up.
  setTimeout(() => {
    qc.invalidateQueries({ queryKey: ['clients'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  }, 3000);
}

export function usePagar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, dbName, body }: MutationCtx & { body: PagarInput }) =>
      api(`/clients/${serverId}/${dbName}/pagar`, { method: 'POST', body }),
    onSuccess: () => refreshAfterMutation(qc),
  });
}

export function useRevertir() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, dbName, body }: MutationCtx & { body: RevertirInput }) =>
      api(`/clients/${serverId}/${dbName}/revertir`, { method: 'POST', body }),
    onSuccess: () => refreshAfterMutation(qc),
  });
}

export function useDias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, dbName, body }: MutationCtx & { body: DiasInput }) =>
      api(`/clients/${serverId}/${dbName}/dias`, { method: 'POST', body }),
    onSuccess: () => refreshAfterMutation(qc),
  });
}

export function useAddDeuda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, dbName, body }: MutationCtx & { body: AddDeudaInput }) =>
      api(`/clients/${serverId}/${dbName}/add_deuda`, { method: 'POST', body }),
    onSuccess: () => refreshAfterMutation(qc),
  });
}

export function useSyncClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, dbName }: MutationCtx) =>
      api(`/clients/${serverId}/${dbName}/sync`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useSyncAll() {
  return useMutation({
    mutationFn: () => api('/sync/all', { method: 'POST' }),
  });
}
